import { SearchResult, AutocompleteResponse, SearchParams } from './types';

interface SearchRequestBody {
  q: string;
  min_price?: number;
  max_price?: number;
  facets: { [key: string]: string[][] };
  include_facets: { [key: string]: string[][] };
  sort?: string;
  boolean_facets?: { [key: string]: boolean } | null;
}

export class HttpApiClient {
  private jwt: string | null = null;
  private loginPromise: Promise<string> | null = null;

  constructor(
    private apiKey: string,
    private clientId: string,
    private baseUrl: string = ''
  ) {}

  public async login(): Promise<string> {
    if (this.jwt) {
      return this.jwt;
    }

    if (!this.loginPromise) {
      this.loginPromise = (async () => {
        try {
          const jwt = await this.apiRequest<string>('/login');
          this.jwt = jwt;
          return jwt;
        } finally {
          this.loginPromise = null;
        }
      })();
    }
    return this.loginPromise;
  }

  private async apiRequest<T>(
    endpoint: string,
    params?: URLSearchParams,
    body?: Record<string, unknown>
  ): Promise<T> {
    const headers: HeadersInit = {
      'x-api-key': this.apiKey,
    };
    if (this.jwt) {
      headers['x-session-id'] = this.jwt;
    }

    if (body) {
      headers['Content-Type'] = 'application/json';
    }

    const fullUrl = this.clientId
      ? `${this.baseUrl}/${this.clientId}${endpoint}`
      : `${this.baseUrl}${endpoint}`;

    const url = params ? `${fullUrl}?${params.toString()}` : fullUrl;

    const response = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers,
      mode: 'cors',
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error:', response.status, errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    // Handle text responses (like login token) that aren't JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return response.text() as unknown as T;
  }

  async fetchAutocomplete(query: string): Promise<AutocompleteResponse> {
    await this.login();
    const params = new URLSearchParams();
    params.append('q', query);
    params.append('threshold', '25');
    params.append('limit', '8');

    return this.apiRequest<AutocompleteResponse>('/api/autocomplete', params);
  }

  async search(searchParams: SearchParams): Promise<SearchResult> {
    await this.login();
    const requestBody: SearchRequestBody = {
      q: searchParams.query,
      facets: searchParams.selectedFacets || {},
      include_facets: searchParams.expandedFacets || {},
      sort: searchParams.sort,
      boolean_facets: searchParams.booleanFacets || null,
    };

    if (searchParams.selectedPriceRange) {
      requestBody.min_price = searchParams.selectedPriceRange[0];
      requestBody.max_price = searchParams.selectedPriceRange[1];
    }

    const bodyRecord = requestBody as unknown as Record<string, unknown>;
    return this.apiRequest<SearchResult>('/api/search', undefined, bodyRecord);
  }
}
